import { AnalyticsEvent } from '@/shared/lib/analytics/events';
import { captureError } from '@/shared/lib/errors/capture';
import posthog from 'posthog-js';
import { toast } from 'sonner';

export type VideoShareSource = 'card' | 'watch_page';

interface ShareVideoLinkInput {
  /** `uuid ?? id` — whatever `/videos/{key}` resolves. */
  videoKey: string | number;
  videoUuid: string | null;
  title: string | null | undefined;
  source: VideoShareSource;
  isAuthenticated: boolean;
  /** Error-report label for the calling component. */
  component: string;
}

/**
 * Share a video's watch URL: the native share sheet where the browser has
 * one (mobile), else copy to clipboard. A dismissed share sheet rejects, so
 * failures are reported at `info` and never surfaced to the viewer.
 */
export function shareVideoLink({
  videoKey,
  videoUuid,
  title,
  source,
  isAuthenticated,
  component,
}: ShareVideoLinkInput): void {
  const shareUrl = `${window.location.origin}/videos/${videoKey}`;
  const canNativeShare = typeof navigator.share === 'function';
  // Insecure contexts and some in-app webviews expose neither API; reading
  // `navigator.clipboard.writeText` there throws synchronously.
  if (!canNativeShare && !navigator.clipboard) {
    toast.error("Sharing isn't available here. Copy the link from the address bar.");
    return;
  }
  const shareMethod = canNativeShare ? 'native_share' : 'clipboard';

  posthog.capture(AnalyticsEvent.VIDEO_SHARED, {
    video_uuid: videoUuid,
    share_method: shareMethod,
    source,
    is_authenticated: isAuthenticated,
  });

  if (canNativeShare) {
    navigator.share({ ...(title ? { title } : {}), url: shareUrl }).catch((error: unknown) => {
      captureError(error, { component, action: 'web-share', severity: 'info', skipPostHog: true });
    });
    return;
  }

  navigator.clipboard
    .writeText(shareUrl)
    .then(() => {
      toast.success('Link copied to clipboard');
    })
    .catch((error: unknown) => {
      captureError(error, {
        component,
        action: 'clipboard-share',
        severity: 'info',
        skipPostHog: true,
      });
    });
}
